import { casual as englishChrono } from 'chrono-node/en';
import { Temporal } from 'temporal-polyfill';

import {
  DateInputError,
  formatIsoUtc,
  formatLocalIso,
  formatSpreadsheet,
  formatUnixMilliseconds,
  formatUnixSeconds,
  parseIsoInstant,
  parseSpreadsheet,
  parseUnixMilliseconds,
  parseUnixSeconds,
  resolvePlainDateTime,
  validateTimeZone,
} from './date-engine.js';
import {
  formatCustom,
  inferCustomCandidates,
  parseCustom,
} from './custom-format.js';

const STRICT_ISO_SHAPE = /^(?:\d{4}|[+-]\d{6})-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:[.,]\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/i;
const ISO_LIKE_WITH_OFFSET = /^(?:\d{4}|[+-]\d{6})-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/i;
const DECIMAL_NUMBER = /^[+-]?\d+(?:\.\d+)?$/;
const PREFIX_PATTERN = /^(?:\/)?(unix-ms|unix|spreadsheet|iso|custom)\/(.*)$/is;
const NUMERIC_NATURAL_TIMEZONE = /\b(?:UTC|GMT)?[+-]\d{1,2}:?\d{2}\b/i;
const TIMEZONE_ABBREVIATIONS = new Set([
  'UTC', 'GMT',
  'EST', 'EDT', 'ET', 'CST', 'CDT', 'CT', 'MST', 'MDT', 'MT', 'PST', 'PDT', 'PT',
  'AKST', 'AKDT', 'HST', 'AST', 'ADT', 'NST', 'NDT',
  'CET', 'CEST', 'EET', 'EEST', 'BST', 'IST', 'MSK',
  'JST', 'KST', 'HKT', 'SGT', 'PHT', 'PKT',
  'AEST', 'AEDT', 'ACST', 'ACDT', 'AWST', 'NZST', 'NZDT',
  'WAT', 'CAT', 'EAT', 'SAST',
]);
const CHAIN_OPERATOR = /\s([+-])\s+/g;
const WEEKDAYS = Object.freeze({
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
});
const DURATION_TERM = /(\d+(?:\.\d+)?)\s*(milliseconds?|msecs?|ms|seconds?|secs?|s|minutes?|mins?|m|hours?|hrs?|h|days?|d|weeks?|w|months?|years?|y)\b/gi;
const MAX_INPUT_LENGTH = 2_048;

function parserError(message, code = 'INVALID_DATE_INPUT', details = {}) {
  return new DateInputError(message, code, details);
}

function requireText(input) {
  if (typeof input !== 'string' || input.trim() === '') {
    throw parserError('Enter a date, timestamp, or English date expression.', 'EMPTY_DATE_INPUT', {
      input,
    });
  }
  const value = input.trim();
  if (value.length > MAX_INPUT_LENGTH) {
    throw parserError(`Date inputs are limited to ${MAX_INPUT_LENGTH} characters.`, 'DATE_INPUT_TOO_LONG', {
      length: value.length,
    });
  }
  return value;
}

function canonicalInstant(value) {
  try {
    // The date engine's formatter intentionally floors negative sub-millisecond
    // instants and is the single millisecond-normalization rule for the app.
    return parseUnixMilliseconds(formatUnixMilliseconds(Temporal.Instant.from(value)));
  } catch (cause) {
    if (cause instanceof DateInputError) throw cause;
    throw parserError('The reference instant is invalid.', 'INVALID_REFERENCE_INSTANT', {
      value,
      cause: cause?.message,
    });
  }
}

function source(kind, raw, extra = {}) {
  return Object.freeze({
    kind,
    type: kind,
    raw,
    ...extra,
  });
}

function parsedResult(instant, provenance, resolution = {}) {
  const normalizedInstant = canonicalInstant(instant);
  const fold = resolution.fold ?? provenance.fold ?? 'none';
  const alternatives = resolution.alternatives;
  const normalizedSource = Object.freeze({ ...provenance, fold });
  return Object.freeze({
    instant: normalizedInstant,
    source: normalizedSource,
    provenance: normalizedSource,
    format: normalizedSource.kind,
    fold,
    ...(alternatives ? { alternatives } : {}),
  });
}

function spreadsheetResolution(text, timeZone, disambiguation) {
  const earlier = parseSpreadsheet(text, timeZone, 'earlier');
  const later = parseSpreadsheet(text, timeZone, 'later');
  if (earlier.equals(later)) return { instant: earlier, fold: 'none' };

  const alternatives = { earlier, later };
  if (disambiguation === 'reject') {
    throw parserError(
      'That spreadsheet wall time occurs twice in this time zone; choose the earlier or later occurrence.',
      'AMBIGUOUS_LOCAL_TIME',
      { value: text, zone: timeZone, alternatives },
    );
  }
  const useLater = disambiguation === 'later';
  return {
    instant: useLater ? later : earlier,
    fold: useLater ? 'later' : 'earlier',
    alternatives,
  };
}

function parseExplicit(type, value, options) {
  const raw = requireText(value);
  const { timeZone, fold, now } = options;

  switch (type) {
    case 'unix':
      return parsedResult(parseUnixSeconds(raw), source('unix-seconds', raw, { explicit: true }));
    case 'unix-ms':
      return parsedResult(parseUnixMilliseconds(raw), source('unix-milliseconds', raw, { explicit: true }));
    case 'spreadsheet': {
      const resolution = spreadsheetResolution(raw, timeZone, fold);
      return parsedResult(
        resolution.instant,
        source('spreadsheet', raw, { explicit: true, timeZone }),
        resolution,
      );
    }
    case 'iso':
      return parsedResult(parseIsoInstant(raw), source('iso', raw, { explicit: true }));
    case 'custom': {
      const dialect = options.dialect;
      const pattern = options.pattern ?? options.fmt;
      if (!dialect || !pattern) {
        throw parserError(
          'Custom routes require both a supported dialect and a format pattern.',
          'CUSTOM_METADATA_REQUIRED',
          { dialect, pattern },
        );
      }
      const resolution = parseCustom(raw, timeZone, dialect, pattern, now, fold);
      return parsedResult(
        resolution.instant,
        source('custom', raw, {
          explicit: true,
          dialect,
          pattern,
          fmt: pattern,
          timeZone,
          ...(options.dateOrderConfirmed ? { dateOrder: options.dateOrder } : {}),
        }),
        resolution,
      );
    }
    default:
      throw parserError(`Unknown explicit date type: ${String(type)}`, 'INVALID_EXPLICIT_DATE_TYPE', {
        type,
      });
  }
}

function parseInferredCustom(value, candidates, options) {
  let candidate;
  if (candidates.length === 1) {
    [candidate] = candidates;
  } else if (options.selectedPattern) {
    candidate = candidates.find((item) => item.pattern === options.selectedPattern);
  } else if (options.resolveAmbiguous || options.dateOrderConfirmed) {
    candidate = candidates.find((item) => item.dateOrder === options.dateOrder);
  }

  if (!candidate) {
    throw parserError(
      'This calendar date can be read in more than one order; choose month/day or day/month.',
      'AMBIGUOUS_CUSTOM_DATE',
      {
        value,
        candidates: candidates.map((item) => ({ ...item })),
        preferredDateOrder: options.dateOrder,
      },
    );
  }

  const resolution = parseCustom(
    value,
    options.timeZone,
    candidate.dialect,
    candidate.pattern,
    options.now,
    options.fold,
  );
  return parsedResult(
    resolution.instant,
    source('custom', value, {
      inferred: true,
      dialect: candidate.dialect,
      pattern: candidate.pattern,
      fmt: candidate.pattern,
      dateOrder: candidate.dateOrder,
      timeZone: options.timeZone,
      candidate,
    }),
    resolution,
  );
}

function parseNumeric(value, options) {
  if (!DECIMAL_NUMBER.test(value)) return null;

  const sign = value[0] === '+' || value[0] === '-' ? value[0] : '';
  const unsigned = sign ? value.slice(1) : value;
  const [whole, fraction] = unsigned.split('.');
  const significantWhole = whole.replace(/^0+(?=\d)/, '');
  const digits = significantWhole.length;

  if (whole.split('').every((character) => character === '0') && !fraction) {
    return parsedResult(parseUnixSeconds(value), source('unix-seconds', value));
  }

  if (!sign && digits === 5) {
    const resolution = spreadsheetResolution(value, options.timeZone, options.fold);
    return parsedResult(
      resolution.instant,
      source('spreadsheet', value, { timeZone: options.timeZone }),
      resolution,
    );
  }

  if (digits >= 12) {
    if (fraction !== undefined) {
      throw parserError(
        'Unix millisecond timestamps must be whole numbers.',
        'INVALID_UNIX_MILLISECONDS',
        { value },
      );
    }
    return parsedResult(parseUnixMilliseconds(value), source('unix-milliseconds', value));
  }

  if (sign || digits >= 6) {
    return parsedResult(parseUnixSeconds(value), source('unix-seconds', value));
  }

  throw parserError(
    'This short number could be a spreadsheet serial or Unix timestamp; use /spreadsheet/ or /unix/.',
    'AMBIGUOUS_NUMERIC_INPUT',
    { value, choices: ['spreadsheet', 'unix'] },
  );
}

function offsetMinutes(offset) {
  const match = /^([+-])(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(offset);
  if (!match) return 0;
  const minutes = Number(match[2]) * 60 + Number(match[3]) + Number(match[4] ?? 0) / 60;
  return match[1] === '-' ? -minutes : minutes;
}

function chronoFullInput(value, reference, options) {
  const hasAbbreviation = value
    .match(/\b[A-Za-z]{2,5}\b/g)
    ?.some((token) => TIMEZONE_ABBREVIATIONS.has(token.toLocaleUpperCase('en-US')));
  if (hasAbbreviation || NUMERIC_NATURAL_TIMEZONE.test(value)) {
    throw parserError(
      'Timezone abbreviations and ad-hoc offsets are ambiguous; choose an IANA timezone instead.',
      'AMBIGUOUS_TIMEZONE_ABBREVIATION',
      { value },
    );
  }

  const referenceZoned = reference.toZonedDateTimeISO(options.timeZone);
  const results = englishChrono.parse(
    value,
    {
      instant: new Date(Number(reference.epochMilliseconds)),
      timezone: offsetMinutes(referenceZoned.offset),
    },
    { forwardDate: true },
  );

  const match = results.length === 1 ? results[0] : undefined;
  if (
    !match ||
    match.index !== 0 ||
    match.text.length !== value.length ||
    match.end
  ) {
    throw parserError(
      'The English date must be one complete date expression, not a fragment or range.',
      'INVALID_NATURAL_LANGUAGE',
      { value, matches: results.map((item) => ({ index: item.index, text: item.text, range: Boolean(item.end) })) },
    );
  }

  if (value.toLocaleLowerCase('en-US') === 'now') {
    return parsedResult(reference, source('natural', value, { timeZone: options.timeZone }));
  }

  const start = match.start;
  const hasExplicitClock = ['hour', 'minute', 'second', 'millisecond', 'meridiem']
    .some((part) => start.isCertain(part));
  const plain = Temporal.PlainDateTime.from({
    year: start.get('year'),
    month: start.get('month'),
    day: start.get('day'),
    hour: hasExplicitClock ? start.get('hour') : referenceZoned.hour,
    minute: hasExplicitClock ? start.get('minute') : referenceZoned.minute,
    second: hasExplicitClock ? start.get('second') : referenceZoned.second,
    millisecond: hasExplicitClock ? start.get('millisecond') : referenceZoned.millisecond,
    microsecond: 0,
    nanosecond: 0,
  }, { overflow: 'reject' });
  const resolution = resolvePlainDateTime(plain, options.timeZone, options.fold);
  return parsedResult(
    resolution.instant,
    source('natural', value, { timeZone: options.timeZone }),
    resolution,
  );
}

function splitExpressionChain(value) {
  const operators = [];
  CHAIN_OPERATOR.lastIndex = 0;
  let match;
  while ((match = CHAIN_OPERATOR.exec(value))) {
    operators.push({ operator: match[1], index: match.index, bodyStart: CHAIN_OPERATOR.lastIndex });
  }
  if (operators.length === 0) return null;

  const base = value.slice(0, operators[0].index).trim();
  if (!base) return null;
  const steps = operators.map((item, index) => ({
    operator: item.operator,
    body: value.slice(item.bodyStart, operators[index + 1]?.index ?? value.length).trim(),
  }));
  if (steps.some((step) => !step.body)) return null;
  return { base, steps };
}

function normalizeUnit(unit) {
  const normalized = unit.toLocaleLowerCase('en-US');
  if (/^(?:milliseconds?|msecs?|ms)$/.test(normalized)) return 'milliseconds';
  if (/^(?:seconds?|secs?|s)$/.test(normalized)) return 'seconds';
  if (/^(?:minutes?|mins?|m)$/.test(normalized)) return 'minutes';
  if (/^(?:hours?|hrs?|h)$/.test(normalized)) return 'hours';
  if (/^(?:days?|d)$/.test(normalized)) return 'days';
  if (/^(?:weeks?|w)$/.test(normalized)) return 'weeks';
  if (/^months?$/.test(normalized)) return 'months';
  if (/^(?:years?|y)$/.test(normalized)) return 'years';
  return null;
}

function parseDurationBody(body) {
  const amounts = {
    years: 0,
    months: 0,
    weeks: 0,
    days: 0,
    milliseconds: 0,
  };
  let cursor = 0;
  let found = false;
  DURATION_TERM.lastIndex = 0;
  let match;

  while ((match = DURATION_TERM.exec(body))) {
    const gap = body.slice(cursor, match.index).replace(/(?:\band\b|,)/gi, '').trim();
    if (gap) return null;
    cursor = DURATION_TERM.lastIndex;
    found = true;
    const amount = Number(match[1]);
    const unit = normalizeUnit(match[2]);
    if (!Number.isFinite(amount) || !unit) return null;

    if (['years', 'months', 'weeks', 'days'].includes(unit)) {
      if (!Number.isSafeInteger(amount)) {
        throw parserError('Calendar offsets must use whole, safely sized units.', 'INVALID_RELATIVE_EXPRESSION', {
          body,
          amount,
          unit,
        });
      }
      amounts[unit] += amount;
    } else {
      const factor = unit === 'hours'
        ? 3_600_000
        : unit === 'minutes'
          ? 60_000
          : unit === 'seconds'
            ? 1_000
            : 1;
      amounts.milliseconds += Math.trunc(amount * factor);
    }
  }

  const tail = body.slice(cursor).replace(/(?:\band\b|,)/gi, '').trim();
  if (!found || tail || !Number.isSafeInteger(amounts.milliseconds)) return null;
  return amounts;
}

function shiftResolution(resolution, milliseconds) {
  if (!milliseconds) return resolution;
  try {
    const shift = (instant) => canonicalInstant(instant.add({ milliseconds }));
    return {
      ...resolution,
      instant: shift(resolution.instant),
      ...(resolution.alternatives
        ? {
            alternatives: {
              earlier: shift(resolution.alternatives.earlier),
              later: shift(resolution.alternatives.later),
            },
          }
        : {}),
    };
  } catch (cause) {
    throw parserError('The clock offset is outside the supported date range.', 'RELATIVE_OUT_OF_RANGE', {
      milliseconds,
      cause: cause?.message,
    });
  }
}

function calendarResolution(baseInstant, timeZone, duration, disambiguation) {
  const zoned = canonicalInstant(baseInstant).toZonedDateTimeISO(timeZone);
  try {
    const target = zoned.toPlainDateTime().add(duration, { overflow: 'constrain' });
    return resolvePlainDateTime(target, timeZone, disambiguation);
  } catch (cause) {
    if (cause instanceof DateInputError) throw cause;
    throw parserError('The calendar offset is outside the supported date range.', 'RELATIVE_OUT_OF_RANGE', {
      duration,
      cause: cause?.message,
    });
  }
}

function relativeResolution(baseInstant, operator, body, options) {
  const sign = operator === '-' ? -1 : 1;
  const weekday = /^(?:(?:next|last|previous)\s+)?(monday|tuesday|wednesday|thursday|friday|saturday|sunday)$/i.exec(body);
  if (weekday) {
    const zoned = canonicalInstant(baseInstant).toZonedDateTimeISO(options.timeZone);
    const target = WEEKDAYS[weekday[1].toLocaleLowerCase('en-US')];
    let days = sign > 0 ? (target - zoned.dayOfWeek + 7) % 7 : -((zoned.dayOfWeek - target + 7) % 7);
    if (days === 0) days = sign * 7;
    return calendarResolution(baseInstant, options.timeZone, { days }, options.fold);
  }

  const duration = parseDurationBody(body);
  if (!duration) {
    throw parserError(
      'Use a weekday or a quantity such as “1 week” or “90 minutes” after the offset sign.',
      'INVALID_RELATIVE_EXPRESSION',
      { operator, body },
    );
  }

  const calendar = {
    years: sign * duration.years,
    months: sign * duration.months,
    weeks: sign * duration.weeks,
    days: sign * duration.days,
  };
  const hasCalendar = Object.values(calendar).some(Boolean);
  const resolved = hasCalendar
    ? calendarResolution(baseInstant, options.timeZone, calendar, options.fold)
    : { instant: canonicalInstant(baseInstant), fold: 'none' };
  return shiftResolution(resolved, sign * duration.milliseconds);
}

/**
 * Format an updated instant in the representation that originally produced it.
 * Natural-language sources deliberately retain their expression instead.
 */
function formatSourceValue(instant, provenance, options = {}) {
  const value = canonicalInstant(instant);
  const format = typeof provenance === 'string' ? provenance : provenance?.kind;
  const timeZone = options.timeZone ?? provenance?.timeZone ?? 'UTC';

  switch (format) {
    case 'unix':
    case 'unix-seconds': return formatUnixSeconds(value);
    case 'unix-ms':
    case 'unix-milliseconds': return formatUnixMilliseconds(value);
    case 'spreadsheet': return formatSpreadsheet(value, timeZone);
    case 'iso': return formatIsoUtc(value);
    case 'local': return formatLocalIso(value, timeZone);
    case 'custom': {
      const dialect = options.dialect ?? provenance?.dialect;
      const pattern = options.pattern ?? options.fmt ?? provenance?.pattern ?? provenance?.fmt;
      return formatCustom(value, timeZone, dialect, pattern);
    }
    case 'natural': return provenance?.raw ?? formatIsoUtc(value);
    default:
      throw parserError(`Cannot format an unknown source type: ${String(format)}`, 'UNKNOWN_SOURCE_FORMAT', {
        format,
      });
  }
}

/**
 * Preferred signature: `formatSourceInstant(resultOrSource, instant, zone)`.
 * The instant-first form is also accepted internally and for small utilities.
 */
export function formatSourceInstant(first, second, third = {}) {
  const sourceFirst = Boolean(
    first?.source ||
    first?.kind ||
    (typeof first === 'string' && !/^[-+]?\d|^\d{4}-/.test(first)),
  );
  if (sourceFirst) {
    const provenance = first.source ?? first;
    const options = typeof third === 'string' ? { timeZone: third } : third;
    return formatSourceValue(second ?? first.instant, provenance, options);
  }
  return formatSourceValue(first, second, third);
}

/** Apply a signed relative expression against an already displayed instant. */
export function applyRelativeExpression(expression, base, rawOptions = {}) {
  const value = requireText(expression);
  const match = /^([+-])\s*(.+)$/.exec(value);
  if (!match) {
    throw parserError('Relative expressions must begin with + or -.', 'INVALID_RELATIVE_EXPRESSION', {
      value,
    });
  }

  const baseResult = base?.instant
    ? base
    : {
        instant: base,
        source: rawOptions.baseSource ?? rawOptions.source ?? rawOptions.provenance,
      };
  if (!baseResult?.instant) {
    throw parserError('Choose a displayed date before applying a relative expression.', 'RELATIVE_BASE_REQUIRED');
  }

  const timeZone = validateTimeZone(rawOptions.timeZone ?? rawOptions.zone ?? baseResult.source?.timeZone ?? 'UTC');
  const fold = rawOptions.fold ?? 'reject';
  const resolution = relativeResolution(baseResult.instant, match[1], match[2].trim(), { timeZone, fold });
  const previousSource = baseResult.source ?? source('iso', formatIsoUtc(baseResult.instant));
  const raw = previousSource.kind === 'natural'
    ? `${previousSource.raw} ${match[1]} ${match[2].trim()}`
    : formatSourceValue(resolution.instant, previousSource, { timeZone });
  const nextSource = source(previousSource.kind, raw, {
    ...previousSource,
    raw,
    timeZone: previousSource.timeZone ?? (['spreadsheet', 'custom', 'local'].includes(previousSource.kind) ? timeZone : undefined),
  });
  return parsedResult(resolution.instant, nextSource, resolution);
}

/** Parse a single UI value. URL-specific plus decoding lives in url-state.js. */
export function parseDateInput(input, rawOptions = {}) {
  const value = requireText(input).replace(/^\/(?!\/)/, '').replace(/\/$/, '');
  const timeZone = validateTimeZone(rawOptions.timeZone ?? rawOptions.zone ?? 'UTC');
  const now = canonicalInstant(rawOptions.now ?? rawOptions.referenceInstant ?? Temporal.Now.instant());
  const dateOrder = rawOptions.dateOrder === 'DMY' ? 'DMY' : 'MDY';
  const fold = ['earlier', 'later', 'reject'].includes(rawOptions.fold) ? rawOptions.fold : 'reject';
  const dialect = rawOptions.dialect ?? rawOptions.customDialect;
  const pattern = rawOptions.pattern ?? rawOptions.fmt ?? rawOptions.customPattern;
  const options = { ...rawOptions, timeZone, now, dateOrder, fold, dialect, pattern };

  const prefixed = PREFIX_PATTERN.exec(value);
  if (prefixed) return parseExplicit(prefixed[1], prefixed[2], options);
  if (rawOptions.explicitType) {
    const explicitType = rawOptions.explicitType === 'unix-seconds'
      ? 'unix'
      : rawOptions.explicitType === 'unix-milliseconds'
        ? 'unix-ms'
        : rawOptions.explicitType;
    return parseExplicit(explicitType, value, options);
  }

  if (STRICT_ISO_SHAPE.test(value) || ISO_LIKE_WITH_OFFSET.test(value)) {
    return parsedResult(parseIsoInstant(value), source('iso', value));
  }

  const candidates = inferCustomCandidates(value, dateOrder, now);
  if (candidates.length > 0) return parseInferredCustom(value, candidates, options);

  const numeric = parseNumeric(value, options);
  if (numeric) return numeric;

  if (rawOptions.allowChains !== false) {
    const chain = splitExpressionChain(value);
    if (chain) {
      if (
        chain.steps.some((step) => (
          step.operator === '-' &&
          /^(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?|\d{1,4}[./-]\d)/i.test(step.body)
        ))
      ) {
        throw parserError('Date ranges are not supported; enter one instant.', 'INVALID_NATURAL_LANGUAGE', {
          value,
        });
      }
      let current = parseDateInput(chain.base, { ...options, allowChains: false });
      for (const step of chain.steps) {
        current = applyRelativeExpression(`${step.operator} ${step.body}`, current, options);
      }
      return current;
    }
  }

  const relative = /^([+-])\s*(.+)$/.exec(value);
  if (relative) {
    const base = rawOptions.base ?? (
      rawOptions.baseInstant
        ? {
            instant: rawOptions.baseInstant,
            source: rawOptions.baseSource ?? rawOptions.source ?? rawOptions.provenance,
          }
        : undefined
    );
    return applyRelativeExpression(value, base, options);
  }

  return chronoFullInput(value, now, options);
}

/** A non-throwing adapter suitable for partial live editing. */
export function tryParseDateInput(input, options = {}) {
  try {
    return { ok: true, value: parseDateInput(input, options) };
  } catch (error) {
    if (!(error instanceof DateInputError)) throw error;
    return { ok: false, error };
  }
}

export const parseInput = parseDateInput;
